/**
 * Rate Verification Utility
 *
 * This module provides tools to verify if fetched rates are accurate
 * by comparing against known market conditions and cross-checking sources
 */

export interface RateVerification {
  currency: string;
  officialRate: number;
  parallelRate: number;
  isValid: boolean;
  warnings: string[];
  confidence: number;
}

// Known market conditions (updated Feb 2026)
const MARKET_CONDITIONS = {
  // Typical premium of parallel market over official rates
  TYPICAL_PREMIUM_MIN: 0.08, // 8%
  TYPICAL_PREMIUM_MAX: 0.15, // 15%

  // Maximum daily volatility (rates shouldn't change more than this per day)
  MAX_DAILY_CHANGE: 0.03, // 3%

  // Reasonable ranges based on current market (Feb 2026)
  REASONABLE_RANGES: {
    USD_OFFICIAL: { min: 1440, max: 1480 },
    USD_PARALLEL: { min: 1580, max: 1680 },
    EUR_OFFICIAL: { min: 1570, max: 1650 },
    EUR_PARALLEL: { min: 1720, max: 1850 },
    GBP_OFFICIAL: { min: 1820, max: 1920 },
    GBP_PARALLEL: { min: 1990, max: 2150 },
  }
};

export function verifyRate(
  currency: string,
  officialRate: number,
  parallelRate: number
): RateVerification {
  const warnings: string[] = [];
  let confidence = 1.0;

  // Check if premium is within typical range
  const premium = (parallelRate - officialRate) / officialRate;

  if (premium < MARKET_CONDITIONS.TYPICAL_PREMIUM_MIN) {
    warnings.push(`Premium too low (${(premium * 100).toFixed(1)}%). Parallel market typically 8-15% higher than official.`);
    confidence -= 0.3;
  }

  if (premium > MARKET_CONDITIONS.TYPICAL_PREMIUM_MAX) {
    warnings.push(`Premium too high (${(premium * 100).toFixed(1)}%). May indicate data error.`);
    confidence -= 0.2;
  }

  // Check against reasonable ranges
  const officialKey = `${currency}_OFFICIAL` as keyof typeof MARKET_CONDITIONS.REASONABLE_RANGES;
  const parallelKey = `${currency}_PARALLEL` as keyof typeof MARKET_CONDITIONS.REASONABLE_RANGES;

  const officialRange = MARKET_CONDITIONS.REASONABLE_RANGES[officialKey];
  const parallelRange = MARKET_CONDITIONS.REASONABLE_RANGES[parallelKey];

  if (officialRange) {
    if (officialRate < officialRange.min) {
      warnings.push(`Official rate ${officialRate} is below expected minimum ${officialRange.min}`);
      confidence -= 0.4;
    }
    if (officialRate > officialRange.max) {
      warnings.push(`Official rate ${officialRate} is above expected maximum ${officialRange.max}`);
      confidence -= 0.4;
    }
  }

  if (parallelRange) {
    if (parallelRate < parallelRange.min) {
      warnings.push(`Parallel rate ${parallelRate} is below expected minimum ${parallelRange.min}`);
      confidence -= 0.4;
    }
    if (parallelRate > parallelRange.max) {
      warnings.push(`Parallel rate ${parallelRate} is above expected maximum ${parallelRange.max}`);
      confidence -= 0.4;
    }
  }

  const isValid = confidence >= 0.5;

  return {
    currency,
    officialRate,
    parallelRate,
    isValid,
    warnings,
    confidence: Math.max(0, confidence)
  };
}

export function verifyRates(rates: Array<{
  currency: string;
  officialRate: number;
  parallelRate: number;
}>): RateVerification[] {
  return rates.map(rate => verifyRate(rate.currency, rate.officialRate, rate.parallelRate));
}

export function logRateVerification(verification: RateVerification[]): void {
  console.group('🔍 Rate Verification Results');

  verification.forEach(v => {
    const status = v.isValid ? '✅' : '❌';
    console.group(`${status} ${v.currency}/NGN (Confidence: ${(v.confidence * 100).toFixed(0)}%)`);
    console.log(`Official: ₦${v.officialRate.toFixed(2)}`);
    console.log(`Parallel: ₦${v.parallelRate.toFixed(2)}`);
    console.log(`Premium: ${(((v.parallelRate - v.officialRate) / v.officialRate) * 100).toFixed(1)}%`);

    if (v.warnings.length > 0) {
      console.warn('Warnings:');
      v.warnings.forEach(w => console.warn(`  - ${w}`));
    }

    console.groupEnd();
  });

  console.groupEnd();
}

/**
 * Get expected rate ranges for display/validation
 */
export function getExpectedRanges() {
  return {
    USD: {
      official: '₦1,440 - ₦1,480',
      parallel: '₦1,580 - ₦1,680'
    },
    EUR: {
      official: '₦1,570 - ₦1,650',
      parallel: '₦1,720 - ₦1,850'
    },
    GBP: {
      official: '₦1,820 - ₦1,920',
      parallel: '₦1,990 - ₦2,150'
    }
  };
}
